import './inbox_page.less';

import * as Business                        from '../business';
import { Navbar as F7Navbar }               from 'framework7-react';
import { useCallback }                      from 'react';
import { useEffect }                        from 'react';
import { useState }                         from 'react';
import classnames                           from 'classnames';
import Conditional                          from '../components/conditional';
import ConversationList                     from './conversation_list';
import Dropdown                             from '../components/dropdown';
import FetchFromServer                      from '../components/fetch_from_server';
import Navbar                               from '../components/navbar';
import QueryString                          from 'query-string';
import React                                from 'react';
import SearchField                          from '../components/search_field';
import useRouter                            from '../hooks/use_router';

import { ArrowRightCircle as AssignedIcon } from 'react-feather';
import { Frown as DetractorsIcon }          from 'react-feather';
import { Eye as FollowingIcon }             from 'react-feather';
import { Flag as LeadsIcon }                from 'react-feather';
import { Meh as PassivesIcon }              from 'react-feather';
import { Smile as PromotersIcon }           from 'react-feather';
import { ThumbsDown as ThumbsDownIcon }     from 'react-feather';
import { ThumbsUp as ThumbsUpIcon }         from 'react-feather';
import UnreadIcon                           from './mark_as_unread_icon.svg';


export default function InboxPage({
  statuses,
  load,
  businessID,
  conversationID,
  currentUserID,
  conversations
}) {

  const [ query, setQuery ]                      = useState('');
  const [ filters, updateFilters, toggleFilter ] = useFilters();

  const filtered         = applyFilters({ conversations, filters, currentUserID });
  const hasConversations = conversations.length > 0;
  const hasFilters       = filters.length > 0;
  const noFilterMatches  = filtered.length === 0;
  const tooManyFilters   = hasConversations && hasFilters && noFilterMatches;

  const classify = Business.useClassifySetting(businessID);
  const isNPS    = (classify === 'nps');

  return (
    <div className="page inbox-page" data-name="conversations">
      <F7Navbar title="Inbox">
        <Navbar.LeftButton label=""/>
        <Navbar.RightButton label="Add" href={`/${businessID}/contacts/add`}/>
      </F7Navbar>
      <FetchFromServer statuses={statuses} load={load}>
        <div className="filter-bar">
          <SearchField {...{ query, setQuery }}/>
          <FilterDropdown {...{ filters, updateFilters, toggleFilter, tooManyFilters, isNPS }}/>
        </div>
        <Conditional condition={tooManyFilters}>
          <NoFilterResults/>
        </Conditional>
        <Conditional condition={!tooManyFilters}>
          <ConversationList {...{
            businessID,
            conversationID,
            currentUserID,
            isNPS,
            conversations: filtered,
            query
          }}/>
        </Conditional>
      </FetchFromServer>
    </div>
  );
}


function useFilters() {
  const router                     = useRouter();
  const initialFilters             = getInitialFilters(router.currentRoute.query);
  const [ filters, updateFilters ] = useState(initialFilters);

  const toggleFilter = useCallback(function toggleFilter(name) {
    updateFilters(prevState => {
      if (prevState.includes(name))
        return prevState.filter(key => key !== name);
      else
        return ([ ...prevState, name ]).sort(sortByFilterOrder);
    });
  }, [ updateFilters ]);

  useEffect(function persistFilters() {
    const query = {};
    if (filters.length)
      query.filters = filters.join(',');

    const queryString = QueryString.stringify(query);
    const path        = router.currentRoute.path;
    const parts       = [ path, queryString ];
    const url         = parts.filter(Boolean).join('?');
    router.updateCurrentUrl(url);
  }, [ router, filters ]);

  return [ filters, updateFilters, toggleFilter ];
}


const filterOrder = [
  'unread',
  'leads',
  'assigned',
  'following',
  'promoters',
  'passives',
  'detractors'
];

function sortByFilterOrder(a, b) {
  const aPos = filterOrder.indexOf(a);
  const bPos = filterOrder.indexOf(b);
  return aPos - bPos;
}


export function getInitialFilters(query) {
  const filters = query.filters || '';
  return filters.split(',').filter(Boolean).sort(sortByFilterOrder);
}


function NoFilterResults() {
  return (
    <div className="no-search-results">
      <h3 className="title">Nothing found</h3>
      <p className="subtitle">No conversations match your filters.</p>
    </div>
  );
}


function FilterDropdown({ filters, updateFilters, toggleFilter, tooManyFilters, isNPS }) {
  const className = classnames('filter-dropdown', {
    'active':  filters.length > 0,
    'error':   tooManyFilters,
    'nps':     isNPS,
    'not-nps': !isNPS
  });

  const filterProps = { filters, updateFilters, toggleFilter, isNPS };

  return (
    <Dropdown className={className}>
      <Dropdown.Header>
        <FilterHeader {...filterProps}/>
      </Dropdown.Header>
      <Dropdown.Content>
        <FilterSection>
          <FilterItem {...filterProps} name="unread"/>
        </FilterSection>
        <FilterSection>
          <FilterItem {...filterProps} name="leads"/>
        </FilterSection>
        <FilterSection>
          <FilterItem {...filterProps} name="assigned"/>
        </FilterSection>
        <FilterSection>
          <FilterItem {...filterProps} name="following"/>
        </FilterSection>
        <Conditional condition={isNPS}>
          <FilterSection>
            <FilterItem {...filterProps} name="promoters"/>
            <FilterItem {...filterProps} name="passives"/>
            <FilterItem {...filterProps} name="detractors"/>
          </FilterSection>
        </Conditional>
        <Conditional condition={!isNPS}>
          <FilterSection>
            <FilterItem {...filterProps} name="promoters"/>
            <FilterItem {...filterProps} name="detractors"/>
          </FilterSection>
        </Conditional>
      </Dropdown.Content>
    </Dropdown>
  );
}


function FilterHeader({ filters, isNPS }) {
  return (
    <>
      <Conditional condition={filters.length === 0}>
        <div className="label">Filter</div>
      </Conditional>
      <Conditional condition={filters.length === 1}>
        <FilterHeaderIcon name={filters[0]} isNPS={isNPS}/>
        <div className="label">
          <FilterLabel name={filters[0]}/>
        </div>
      </Conditional>
      <Conditional condition={filters.length > 1}>
        {filters.map(name => <FilterHeaderIcon key={name} name={name} isNPS={isNPS}/>)}
      </Conditional>
    </>
  );
}


function FilterHeaderIcon({ name, isNPS }) {
  const className = classnames('icon', name);
  return (
    <div className={className}>
      <FilterIcon name={name} isNPS={isNPS}/>
    </div>
  );
}


function FilterSection({ children }) {
  return (
    <div className="filter-section">
      {children}
    </div>
  );
}


const exclusiveFilters = [ 'promoters', 'passives', 'detractors' ];

function FilterItem({ name, filters, updateFilters, toggleFilter, isNPS }) {
  const active    = filters.includes(name);
  const className = classnames('filter-item', name, { active });

  function onClick() {
    toggleFilter(name);

    if (exclusiveFilters.includes(name)) {
      const otherFilters = exclusiveFilters.filter(filterName => filterName !== name);
      updateFilters(prevState => prevState.filter(filterName => !otherFilters.includes(filterName)));
    }
  }

  return (
    <div className={className} onClick={onClick}>
      <div className="icon"><FilterIcon {...{ name, isNPS }}/></div>
      <div className="label"><FilterLabel {...{ name }}/></div>
    </div>
  );
}


function FilterIcon({ name, isNPS }) {
  switch (name) {
    case 'unread':
      return <UnreadIcon/>;
    case 'leads':
      return <LeadsIcon/>;
    case 'assigned':
      return <AssignedIcon/>;
    case 'following':
      return <FollowingIcon/>;
    case 'promoters':
      return isNPS ? <PromotersIcon/> : <ThumbsUpIcon/>;
    case 'passives':
      return <PassivesIcon/>;
    case 'detractors':
      return isNPS ? <DetractorsIcon/> : <ThumbsDownIcon/>;
    default:
      return null;
  }
}


function FilterLabel({ name }) {
  switch (name) {
    case 'unread':
      return 'Unread';
    case 'leads':
      return 'Leads';
    case 'assigned':
      return 'Assigned';
    case 'following':
      return 'Following';
    case 'promoters':
      return 'Promoters';
    case 'passives':
      return 'Passives';
    case 'detractors':
      return 'Detractors';
    default:
      return null;
  }
}

function applyFilters({ conversations, filters, currentUserID }) {
  if (filters.length) {
    return conversations.filter(conversation => {
      return filters.every(filterName => applyFilter({ conversation, filterName, currentUserID }));
    });
  } else
    return conversations;
}

function applyFilter({ conversation, filterName, currentUserID }) {
  const [ customer ] = conversation.customers;
  switch (filterName) {
    case 'unread':
      return isUnread({ conversation });
    case 'leads':
      return isLead({ customer });
    case 'assigned':
      return isAssignedToMe({ conversation, currentUserID });
    case 'following':
      return isFollowing({ conversation, currentUserID });
    case 'promoters':
      return isPromoter({ customer });
    case 'passives':
      return isPassive({ customer });
    case 'detractors':
      return isDetractor({ customer });
    default:
      return false;
  }
}

function isUnread({ conversation }) {
  const unreadCount = conversation?.unreadCount ?? 0;
  return unreadCount > 0;
}

function isLead({ customer }) {
  return customer.status === 'lead';
}

function isAssignedToMe({ conversation, currentUserID }) {
  const [ assignee ] = conversation.assignees;
  return assignee?.user.id === currentUserID;
}

function isFollowing({ conversation, currentUserID }) {
  const participants = conversation?.participants ?? [];
  return participants.some(participant => participant.user.id === currentUserID);
}

function isPromoter({ customer }) {
  if (typeof customer?.npsScore === 'number')
    return customer?.npsScore > 8;
  else
    return customer?.promoter === true;
}

function isPassive({ customer }) {
  return customer?.npsScore === 7 || customer?.npsScore === 8;
}

function isDetractor({ customer }) {
  if (typeof customer?.npsScore === 'number')
    return customer?.npsScore < 7;
  else
    return customer?.promoter === false;
}
